<template>
    <ds-modal
        is-open
        data-qa="rescheduleBroadcastDialog"
        show-close
        :title="$options.MESSAGE_CONSTANTS.RESCHEDULE_MODAL_TITLE"
        @close="modalClose"
    >
        <div>
            <div class="reschedule-description">
                {{ $options.MESSAGE_CONSTANTS.RESCHEDULE_MODAL_DESCRIPTION }}
            </div>

            <div class="date-holder">
                <ds-date-picker
                    v-model="date"
                    data-qa="reschedule-date-picker"
                    class="reschedule-date-picker"
                    disable-before-date="today"
                />

                <ds-date-picker
                    v-model="time"
                    data-qa="reschedule-time-picker"
                    class="reschedule-time-picker"
                    time-only
                />

                <ds-multiselect
                    v-model="selectedTimeZone"
                    data-qa="time-zone-picker"
                    class="timezone-dropdown"
                    placeholder="Choose timezone to send"
                    searchable
                    :options="validTimeZones"
                    bind-value-only
                />
            </div>

            <div v-if="!loading && isInPast" class="date-warning">
                <ds-inline-alert data-qa="past-message-warning" leading-icon type="warning">
                    {{ $options.MESSAGE_CONSTANTS.PAST_MESSAGE_WARNING }}
                </ds-inline-alert>
            </div>

            <div class="reschedule-buttons-container">
                <ds-button
                    :loading="loading"
                    data-qa="confirm-button"
                    class="reschedule-date"
                    @click="reschedule"
                >
                    {{ $options.MESSAGE_CONSTANTS.CONFIRM_BUTTON }}
                </ds-button>

                <ds-text-button @click="modalClose">
                    {{ $options.MESSAGE_CONSTANTS.CANCEL_BUTTON }}
                </ds-text-button>
            </div>
        </div>
    </ds-modal>
</template>

<script>
import moment from 'moment';
import {
    getLocalizationInfo, 
    rescheduleBroadcast,
    getUnlayerEmailBroadcastById,
} from '@/api/broadcasts/emailBroadcast';
import { isScheduleDatePast } from '../utils/emailBroadcast.util';
import {
    formattedDateTime,
} from '../utils/date.util';

import {
    CONTACT_TIME_ZONE_OPTION,
    SCHEDULE_DATE_FORMAT,
} from '../constants/emailBroadcast.constants';

export const MESSAGE_CONSTANTS = {
    PAST_MESSAGE_WARNING: 'You have selected a date and time in the past. If you continue, your broadcast will be sent immediately.',
    RESCHEDULE_MODAL_TITLE: 'Reschedule email broadcast',
    RESCHEDULE_MODAL_DESCRIPTION: 'When do you want to send this broadcast?',
    CONFIRM_BUTTON: 'Reschedule',
    CANCEL_BUTTON: 'Close',
};

export default {
    MESSAGE_CONSTANTS,
    props: {
        userLocale: {
            type: String,
            default: '',
        },
        mailBatchId: [String, Number],
        broadcastId: [String, Number],
    },

    data() {
        return {
            appTimeZone: '',
            date: null,
            time: null,
            validTimeZones: [],
            loading: false,
            errorLoading: false,
            selectedTimeZone: null,
        };
    },

    async created() {
        this.loading = true;

        try {
            await Promise.all([
                this.setLocalizationInfo(),
                this.setOriginalScheduleDate(),
            ]);
        } catch (e) {
            this.errorLoading = true;
        } finally {
            this.loading = false;
        }
    },

    computed: {
        isInPast() {
            const timeZoneId = this.useContactTimeZone ? this.appTimeZone : this.selectedTimeZone;

            return this.time && isScheduleDatePast({
                scheduleDate: formattedDateTime(this.date, this.time, this.userLocale),
                useContactTimeZone: this.useContactTimeZone,
                timeZoneId,
            });
        },

        useContactTimeZone() {
            return this.selectedTimeZone === CONTACT_TIME_ZONE_OPTION;
        },
    },

    methods: {
        async setLocalizationInfo() {
            const {
                timeZoneOptions: unorganizedTimeZones,
                localizationInfo,
            } = await getLocalizationInfo();

            this.validTimeZones = Object.keys(unorganizedTimeZones).map((timeZoneKey) => {
                return { label: unorganizedTimeZones[timeZoneKey], value: timeZoneKey };
            });

            this.validTimeZones.push({
                label: 'Use Contact Time Zone',
                value: CONTACT_TIME_ZONE_OPTION,
            });

            this.appTimeZone = localizationInfo.appTimeZone;
        },

        async setOriginalScheduleDate() {
            const broadcast = await getUnlayerEmailBroadcastById(this.broadcastId);
            const scheduleDate = moment(broadcast.scheduleDateWithoutTimeZone, SCHEDULE_DATE_FORMAT);

            this.date = scheduleDate.toDate();
            this.time = scheduleDate.format('HHmm');
            this.selectedTimeZone = broadcast.useContactTimeZone
                ? CONTACT_TIME_ZONE_OPTION
                : broadcast.timeZoneId;
        },

        async reschedule() {
            this.loading = true;

            try {
                const date = moment(this.date).format('YYYY-MM-DD');
                const scheduleDateWithoutTimeZone = moment(`${date} ${this.time}`, 'YYYY-MM-DD HHmm').format(SCHEDULE_DATE_FORMAT);

                const { data } = await rescheduleBroadcast(
                    this.mailBatchId,
                    scheduleDateWithoutTimeZone,
                    this.selectedTimeZone,
                );

                if (!data) {
                    throw new Error('Error response from server.');
                }

                const useContactTimeZone = this.selectedTimeZone === CONTACT_TIME_ZONE_OPTION;

                this.$emit('reschedule', {
                    scheduleDateWithoutTimeZone,
                    useContactTimeZone,
                    scheduledTimeZoneId: useContactTimeZone ? null : this.selectedTimeZone,
                });
            } catch (e) {
                this.$error({ message: this.$t('unlayerEmail.reschedule.error') });
                this.$emit('error');
            }
        },

        modalClose() {
            this.$emit('close');
        },
    },
};
</script>

<style lang="scss">
    .reschedule-description {
        margin-bottom: 1rem;
    }

    .timezone-dropdown {
        --input-padding-top: 0;
    }

    .reschedule-time-picker {
        width: 300px;
        margin-right: 1rem;
    }

    .reschedule-date-picker {
        width: 500px;
        margin-right: 1rem;
    }

    .reschedule-date {
      margin-right: 1rem;
    }

    .date-warning {
         margin-bottom: 1.5rem;
     }

    .date-holder {
      display: flex;
    }

    .reschedule-buttons-container {
        display: flex;
    }
</style>
